import { useEffect, useRef } from 'react'

// Adapted for Celeiro from Saadat Ali's free “Fluid Glass Button” Framer component.
// Original: https://www.framer.com/marketplace/components/fluid-glass-button/
// License: Framer Limited Commercial License (free content), used inside this project.
const vertexShader = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`

const fragmentShader = `
precision highp float;
varying vec2 vUv;
uniform float uTime;
uniform float uHover;
uniform float uClick;
uniform vec3 uBaseColor;
uniform vec3 uGlassColor;
uniform vec2 uResolution;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  float f = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    f += amp * noise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return f;
}

void main() {
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 p = vUv * 2.0 - 1.0;
  p.x *= aspect;
  vec2 center = vec2(0.5);
  vec2 dirToCenter = normalize(vUv - center + vec2(0.0001));
  float distToCenter = length(vUv - center);
  float radius = 1.0;
  vec2 bounds = vec2(max(aspect - 1.0, 0.0), 0.0);
  vec2 delta = abs(p) - bounds;
  float dist = length(max(delta, 0.0)) + min(max(delta.x, delta.y), 0.0) - radius;
  float innerDist = clamp(abs(dist), 0.0, 1.0);
  vec2 noiseUv = vUv * vec2(2.0, 1.0);
  vec2 warp = vec2(fbm(noiseUv + uTime * 0.5), fbm(noiseUv + uTime * 0.5 + 12.34)) * mix(0.0, 0.4, uHover);
  warp -= dirToCenter * uClick * 0.25 * smoothstep(0.8, 0.0, distToCenter);
  float n1 = fbm(noiseUv + warp + vec2(uTime, 0.0));
  float n2 = fbm(noiseUv + warp + vec2(n1, uTime * 1.2));
  float rimWidth = mix(0.15, 0.35, n2) * mix(1.0, 1.4, uHover) + uClick * 0.15;
  float rim = smoothstep(rimWidth, 0.0, innerDist);
  float specular = smoothstep(0.03, 0.0, abs(innerDist - 0.12 + n1 * 0.08));
  float rightBias = smoothstep(0.2, 1.0, vUv.x);
  rim *= mix(0.6, 1.5, rightBias);
  specular *= mix(0.5, 2.0, rightBias);
  vec2 starUv = vUv * vec2(aspect * 6.0, 6.0);
  starUv.x -= uTime * 0.2;
  starUv.y += sin(uTime * 0.5 + starUv.x) * mix(0.2, 0.6, uHover);
  starUv += dirToCenter * uClick * 1.5;
  vec2 id = floor(starUv);
  vec2 gv = fract(starUv) - 0.5;
  float nStar = hash(id);
  float star = 0.0;
  if (nStar > mix(0.94, 0.86, uHover)) {
    float sizeMod = mix(0.5, 2.5, hash(id + 13.37));
    vec2 wiggle = vec2(sin(uTime * 2.0 + nStar * 50.0), cos(uTime * 2.3 + nStar * 40.0)) * 0.25 * uHover;
    float starDist = length(gv - wiggle) * sizeMod;
    star = smoothstep(0.12, 0.0, starDist) + smoothstep(0.25, 0.0, starDist) * 0.3;
    star *= sin(uTime * mix(5.0, 15.0, hash(id + 42.0)) + nStar * 100.0) * 0.5 + 0.5;
    star *= smoothstep(0.05, 0.2, innerDist);
  }
  vec3 color = uBaseColor;
  color += uGlassColor * smoothstep(0.2, 0.9, n2) * (1.0 - innerDist) * mix(0.2, 0.45, uHover);
  color += uGlassColor * rim * mix(0.6, 1.2, uHover);
  color += vec3(1.0) * specular * mix(0.8, 2.0, uHover);
  color += vec3(1.0) * star * mix(0.8, 1.5, uHover);
  color += uGlassColor * rim * uClick * 0.8;
  color += vec3(1.0) * specular * uClick * 1.5;
  color += uGlassColor * exp(-distToCenter * 6.0) * uClick * 0.6;
  color *= smoothstep(1.5, 0.2, length(vUv - 0.5));
  gl_FragColor = vec4(color, 1.0);
}`

function rgb(hex: string) {
  const value = Number.parseInt(hex.replace('#', ''), 16)
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255] as const
}

export function FluidGlassSurface({ baseColor = '#050505', glassColor = '#c8ffe2', hoverSpeed = 0.6 }: { baseColor?: string; glassColor?: string; hoverSpeed?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = canvasRef.current
    const button = canvas?.parentElement
    const gl = canvas?.getContext('webgl', { antialias: false })
    if (!canvas || !button || !gl) return
    const compile = (type:number, source:string) => { const shader=gl.createShader(type)!;gl.shaderSource(shader,source);gl.compileShader(shader);return shader }
    const program=gl.createProgram()!
    gl.attachShader(program,compile(gl.VERTEX_SHADER,vertexShader));gl.attachShader(program,compile(gl.FRAGMENT_SHADER,fragmentShader));gl.linkProgram(program);gl.useProgram(program)
    const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW)
    const position=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(position);gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0)
    const uniforms={time:gl.getUniformLocation(program,'uTime'),hover:gl.getUniformLocation(program,'uHover'),click:gl.getUniformLocation(program,'uClick'),base:gl.getUniformLocation(program,'uBaseColor'),glass:gl.getUniformLocation(program,'uGlassColor'),resolution:gl.getUniformLocation(program,'uResolution')}
    gl.uniform3fv(uniforms.base,rgb(baseColor));gl.uniform3fv(uniforms.glass,rgb(glassColor))
    let hovered=0,hoverTarget=0,clicked=0,elapsed=0,last=performance.now(),frame=0,visible=true
    const enter=()=>{hoverTarget=1},leave=()=>{hoverTarget=0},down=()=>{clicked=1},up=()=>undefined
    button.addEventListener('pointerenter',enter);button.addEventListener('pointerleave',leave);button.addEventListener('pointerdown',down);window.addEventListener('pointerup',up)
    const resize=()=>{const rect=canvas.getBoundingClientRect(),dpr=Math.min(devicePixelRatio,2);canvas.width=Math.max(1,Math.round(rect.width*dpr));canvas.height=Math.max(1,Math.round(rect.height*dpr));gl.viewport(0,0,canvas.width,canvas.height)}
    const resizeObserver=new ResizeObserver(resize);resizeObserver.observe(canvas);resize()
    const intersection=new IntersectionObserver(([entry])=>{visible=entry.isIntersecting});intersection.observe(canvas)
    const reduce=matchMedia('(prefers-reduced-motion: reduce)').matches
    const render=(now:number)=>{const delta=Math.min((now-last)/1000,.05);last=now;hovered+=(hoverTarget-hovered)*Math.min(1,delta*4);clicked+=(0-clicked)*Math.min(1,delta*6);if(!reduce)elapsed+=delta*(.15+(hoverSpeed-.15)*hovered);gl.uniform1f(uniforms.time,elapsed);gl.uniform1f(uniforms.hover,hovered);gl.uniform1f(uniforms.click,clicked);gl.uniform2f(uniforms.resolution,canvas.width,canvas.height);visible&&gl.drawArrays(gl.TRIANGLES,0,6);frame=requestAnimationFrame(render)}
    frame=requestAnimationFrame(render)
    return()=>{cancelAnimationFrame(frame);resizeObserver.disconnect();intersection.disconnect();button.removeEventListener('pointerenter',enter);button.removeEventListener('pointerleave',leave);button.removeEventListener('pointerdown',down);window.removeEventListener('pointerup',up);gl.deleteProgram(program);gl.deleteBuffer(buffer)}
  },[baseColor,glassColor,hoverSpeed])
  return <canvas ref={canvasRef} className="fluid-glass-canvas" aria-hidden="true" />
}
